import { afterNextRender, inject, Injector } from '@angular/core';

export interface DatatableViewStateSnapshot {
  searchTerm: string;
  page: number;
  pageSize: number;
}

interface DatatableStateLike {
  page?: number;
  pageSize?: number;
  totalPages?: number;
}

interface KtDatatableInternals {
  _theadElement?: HTMLTableSectionElement | null;
  _config?: {
    columns?: Record<string, Record<string, unknown>>;
  };
}

export class DatatableController {
  private static readonly READY_RETRY_FRAMES = 8;

  private readonly injector?: Injector;

  /**
   * KTDataTable detaches the `<tbody>` Angular renders and replaces it with one of its own,
   * so the template-owned rows keep receiving Angular updates while being invisible. We cache
   * that detached body per table element and re-attach it before every KT init so the plugin
   * always reads the current template output instead of its own stale copy.
   */
  private angularTableBody?: HTMLTableSectionElement;
  private angularTableElement?: HTMLTableElement;

  constructor(private readonly datatableId: string) {
    this.injector = DatatableController.tryInjectInjector();
  }

  initialize(snapshot?: DatatableViewStateSnapshot): void {
    // The app runs zoneless, so a bare requestAnimationFrame can win the race against
    // Angular's change detection and let KT snapshot stale rows. Wait for the render.
    this.afterRender(() => {
      this.restoreAngularTableBody();
      this.reinitializeDatatable();

      if (snapshot) {
        this.restoreDatatableViewStateAfterInit(snapshot);
        return;
      }

      this.resetDatatableViewStateAfterInit();
    });
  }

  captureViewState(): DatatableViewStateSnapshot {
    try {
      const instance = this.getDatatableInstance();
      const state = this.tryGetDatatableState(instance);

      return {
        searchTerm: this.getDatatableSearchInput()?.value ?? '',
        page: this.normalizePositiveNumber(state?.page, 1),
        pageSize: this.normalizePositiveNumber(state?.pageSize, 20),
      };
    } catch {
      return {
        searchTerm: this.getDatatableSearchInput()?.value ?? '',
        page: 1,
        pageSize: 20,
      };
    }
  }

  private static tryInjectInjector(): Injector | undefined {
    try {
      return inject(Injector);
    } catch {
      // Instantiated outside an injection context; fall back to requestAnimationFrame.
      return undefined;
    }
  }

  private afterRender(callback: () => void): void {
    if (!this.injector) {
      requestAnimationFrame(callback);
      return;
    }

    afterNextRender(callback, { injector: this.injector });
  }

  private restoreAngularTableBody(): void {
    const tableElement = this.getTableElement();
    if (!tableElement) {
      return;
    }

    // A fresh Angular render rebuilds the whole table, so re-capture its body.
    if (this.angularTableElement !== tableElement) {
      this.angularTableElement = tableElement;
      this.angularTableBody = tableElement.tBodies[0];
      return;
    }

    const angularTableBody = this.angularTableBody;
    if (!angularTableBody || angularTableBody.parentElement === tableElement) {
      return;
    }

    for (const body of Array.from(tableElement.tBodies)) {
      body.remove();
    }

    tableElement.appendChild(angularTableBody);
  }

  /**
   * KT's built-in empty notice ("No records found") is a bare cell whose colspan is guessed
   * from row data (often 1) and is left-aligned. Span it across every header column and center it.
   */
  private static patchEmptyNotice(): void {
    const prototype = (window.KTDataTable as { prototype?: Record<string, unknown> } | undefined)?.prototype;
    if (!prototype || prototype['__fridaEmptyNoticePatched']) {
      return;
    }

    prototype['__fridaEmptyNoticePatched'] = true;
    prototype['_noticeOnTable'] = function (this: { _tableElement: HTMLTableElement }, message = '') {
      const table = this._tableElement;
      const headerCells = table.tHead?.rows[0]?.cells.length ?? 0;
      const cell = table.tBodies[0].insertRow().insertCell();
      cell.colSpan = Math.max(headerCells, 1);
      cell.className = 'text-center text-secondary-foreground py-6';
      cell.style.textAlign = 'center';
      cell.innerHTML = message;
    };
  }

  /**
   * KT sorts local tables by the rendered cell HTML, so formatted dates and "USD 1,000" style
   * amounts sort as text. Columns whose header has `data-kt-datatable-sort-raw` instead sort by
   * the numeric `data-sort-value` attribute that the template puts inside each cell.
   * `_updateData` runs before every local load/sort, so the columns are always registered in time.
   */
  private static patchRawValueSorting(): void {
    const prototype = (window.KTDataTable as { prototype?: Record<string, unknown> } | undefined)?.prototype;
    if (!prototype || prototype['__fridaRawSortPatched'] || typeof prototype['_updateData'] !== 'function') {
      return;
    }

    prototype['__fridaRawSortPatched'] = true;
    const originalUpdateData = prototype['_updateData'] as (...args: unknown[]) => unknown;
    prototype['_updateData'] = function (this: KtDatatableInternals, ...args: unknown[]) {
      DatatableController.registerRawSortColumns(this);
      return originalUpdateData.apply(this, args);
    };
  }

  private static registerRawSortColumns(instance: KtDatatableInternals): void {
    const headers = Array.from(
      instance._theadElement?.querySelectorAll<HTMLTableCellElement>('th[data-kt-datatable-column]') ?? [],
    );
    if (!instance._config || !headers.some((header) => header.hasAttribute('data-kt-datatable-sort-raw'))) {
      return;
    }

    // Once `columns` is set KT renders only the configured columns and writes cells as text unless
    // a `render` is given, so every column is registered (in header order) and keeps its HTML.
    const existing = instance._config.columns ?? {};
    const columns: Record<string, Record<string, unknown>> = {};
    headers.forEach((header) => {
      const field = header.getAttribute('data-kt-datatable-column')!;
      columns[field] = {
        render: DatatableController.renderRawCell,
        ...existing[field],
        ...(header.hasAttribute('data-kt-datatable-sort-raw')
          ? { sortValue: DatatableController.readRawSortValue }
          : {}),
      };
    });
    instance._config.columns = columns;
  }

  private static renderRawCell(cellHtml: unknown): string {
    return cellHtml == null ? '' : String(cellHtml);
  }

  private static readRawSortValue(cellHtml: unknown): number {
    const match = /data-sort-value="([^"]*)"/.exec(String(cellHtml ?? ''));
    const value = match ? Number(match[1]) : Number.NaN;
    return match && match[1] !== '' && Number.isFinite(value) ? value : Number.NaN;
  }

  private reinitializeDatatable(): void {
    DatatableController.patchEmptyNotice();
    DatatableController.patchRawValueSorting();
    // Full reinit: it also clears KT's per-element data so createInstances() can rebuild.
    window.KTDataTable?.reinit();
    window.KTDataTable?.createInstances();
  }

  private getTableElement(): HTMLTableElement | null {
    return document
      .getElementById(this.datatableId)
      ?.querySelector<HTMLTableElement>('table[data-kt-datatable-table="true"]') ?? null;
  }

  private restoreDatatableViewStateAfterInit(snapshot: DatatableViewStateSnapshot): void {
    // Keep input in sync right away; KT-side state is applied once ready.
    this.applyDatatableSearchInput(snapshot.searchTerm, false);

    this.runWhenDatatableReady((instance) => {
      instance.setPageSize?.(snapshot.pageSize);
      instance.search?.(snapshot.searchTerm);

      const latestState = this.tryGetDatatableState(instance);
      const totalPages = this.normalizePositiveNumber(latestState?.totalPages, 1);
      const nextPage = Math.min(this.normalizePositiveNumber(snapshot.page, 1), totalPages);
      instance.goPage?.(nextPage);
      this.applyDatatableSearchInput(snapshot.searchTerm, true);
    });
  }

  private resetDatatableViewStateAfterInit(): void {
    this.applyDatatableSearchInput('', false);
    this.runWhenDatatableReady((instance) => {
      instance.search?.('');
      instance.goPage?.(1);
      this.applyDatatableSearchInput('', true);
    });
  }

  private applyDatatableSearchInput(searchTerm: string, emitEvents: boolean): void {
    const input = this.getDatatableSearchInput();
    if (!input) {
      return;
    }

    input.value = searchTerm;
    if (!emitEvents) {
      return;
    }

    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new KeyboardEvent('keyup', { bubbles: true }));
  }

  private getDatatableSearchInput(): HTMLInputElement | null {
    return document.querySelector(
      `[data-kt-datatable-search="#${this.datatableId}"]`,
    ) as HTMLInputElement | null;
  }

  private getDatatableInstance(): KTDataTableInstance | undefined {
    const datatableElement = document.getElementById(this.datatableId);
    if (!datatableElement) {
      return undefined;
    }

    return window.KTDataTable?.getInstance?.(datatableElement);
  }

  private tryGetDatatableState(instance: KTDataTableInstance | undefined): DatatableStateLike | undefined {
    if (!instance) {
      return undefined;
    }

    try {
      return instance.getState?.();
    } catch {
      return undefined;
    }
  }

  private runWhenDatatableReady(
    callback: (instance: KTDataTableInstance) => void,
    remainingFrames = DatatableController.READY_RETRY_FRAMES,
  ): void {
    const attempt = () => {
      const instance = this.getDatatableInstance();
      const state = this.tryGetDatatableState(instance);

      if (instance && state) {
        callback(instance);
        return;
      }

      if (remainingFrames <= 0) {
        return;
      }

      remainingFrames -= 1;
      requestAnimationFrame(attempt);
    };

    requestAnimationFrame(attempt);
  }

  private normalizePositiveNumber(value: unknown, fallback: number): number {
    return typeof value === 'number' && Number.isFinite(value) && value > 0
      ? value
      : fallback;
  }
}
